import React, { useState, useEffect, useContext } from "react";
import DynamicIcon from "../../components/dynamicicon";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisEmptyList from "../../components/controls/emptylist";
import Pagenated from "../../components/pagenated";
import PdfViewer from "../../components/controls/pdfviewer";
import JisDialogForm from "../../components/formdialog";
import wordicon from "../../assets/ms_word.png";
import excelicon from "../../assets/ms_excel.png";
import pdficon from "../../assets/pdf.png";

// Cashier Financial Report: paid case fees (JISE...) and other payments (JISI...),
// each split into its Internal and External portion.

const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
  { key: "any", label: "Any Date" },
];

const periodRange = (key) => {
  const now = new Date();
  if (key === "any") return { fromDate: "", toDate: "" };
  if (key === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday
    return { fromDate: toYmd(start), toDate: toYmd(now) };
  }
  if (key === "month") {
    return { fromDate: toYmd(new Date(now.getFullYear(), now.getMonth(), 1)), toDate: toYmd(now) };
  }
  return { fromDate: toYmd(now), toDate: toYmd(now) };
};

const CLEAR_DATE_CSS = `.input-wrapper .clear-date{border:none;background:transparent;cursor:pointer;padding:0 4px;display:flex;align-items:center;color:inherit;opacity:.6}.input-wrapper .clear-date:hover{opacity:1}`;

const TYPE_STYLE = {
  Internal: { color: "#0b5394", background: "#e8f1fb", border: "1px solid #b6d3f2" },
  External: { color: "#8a4b00", background: "#fff4e5", border: "1px solid #f5cf99" },
};

const money = (v) => `N ${Constants.formatCurrency(v || 0)}`;

function CashierReport() {
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const [period, setPeriod] = useState("today");
  const [filters, setFilters] = useState({
    ...periodRange("today"),
    paymentType: "all",
    divisionId: "",
    search: "",
  });
  const [applied, setApplied] = useState(null); // filters of the report on screen
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [notFound, setNotFound] = useState("");
  const [count, setCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(Constants.PAGESIZE);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const body = (f) => ({
    fromDate: f.fromDate || null,
    toDate: f.toDate || null,
    paymentType: f.paymentType,
    divisionId: f.divisionId ? Number(f.divisionId) : null,
    search: f.search?.trim() || null,
  });

  const load = async (f, pIndex, pSize) => {
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(
        `${Constants.apiUrl}/otherreport/cashier/${pIndex}/${pSize}`,
        {
          method: "POST",
          body: JSON.stringify(body(f)),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await resp.json();
      setLoading(false);
      if (data.code === 0) {
        setItems(data.items || []);
        setCount(data.totalCount || 0);
        setSummary(data.summary || null);
        setNotFound(data.notFoundReason || "");
      } else if (data.code === -10) {
        setIsExpired(true);
      } else {
        setErr(data.status || "Unable to load the report");
      }
    } catch (e) {
      setLoading(false);
      console.error(e);
      setErr(Constants.serverErr);
    }
  };

  // first load: today's transactions
  useEffect(() => {
    setApplied(filters);
    load(filters, 0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGenerate = () => {
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      setErr("The start date must be before the end date");
      return;
    }
    if (!filters.fromDate && !filters.toDate && !filters.search?.trim()) {
      setErr("Enter a search (payer, suit number, bill reference or transaction ID) to look across all dates, or pick a period.");
      return;
    }
    setApplied(filters);
    setPageIndex(0);
    load(filters, 0, pageSize);
  };

  const onPeriod = (key) => {
    setPeriod(key);
    if (key !== "custom") setFilters({ ...filters, ...periodRange(key) });
  };

  // clear one date; with both cleared the report looks across all dates (needs a search)
  const clearDate = (name) => {
    const next = { ...filters, [name]: "" };
    setFilters(next);
    setPeriod(!next.fromDate && !next.toDate ? "any" : "custom");
  };

  const onChange = (e) => {
    const next = { ...filters, [e.target.name]: e.target.value };
    if (e.target.name === "fromDate" || e.target.name === "toDate") setPeriod("custom");
    // typing a search looks across all dates, unless the user has set their own dates
    if (e.target.name === "search" && e.target.value.trim() && period !== "custom" && period !== "any") {
      next.fromDate = "";
      next.toDate = "";
      setPeriod("any");
    }
    setFilters(next);
  };

  const onPagePick = (pick) => {
    const p = pick.id - 1;
    setPageIndex(p);
    if (applied) load(applied, p, pageSize);
  };

  const onPageSize = (k) => {
    setPageSize(k);
    setPageIndex(0);
    if (applied) load(applied, 0, k);
  };

  const download = async (url, options, filename) => {
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(url, options);
      if (resp.status === 409) {
        const d = await resp.json().catch(() => ({}));
        if (d.code === -10) setIsExpired(true);
        else setErr(d.status || "Access denied");
        return;
      }
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (e) {
      console.error(e);
      setErr("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onExport = (doctype) => {
    if (!applied) return;
    const ext = { excel: "xlsx", msword: "docx", pdf: "pdf" }[doctype];
    download(
      `${Constants.apiUrl}/otherreport/cashier/export/${doctype}`,
      {
        method: "POST",
        body: JSON.stringify(body(applied)),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      },
      applied.fromDate || applied.toDate ? `cashier-report-${applied.fromDate}-${applied.toDate}.${ext}` : `cashier-report-search.${ext}`,
    );
  };

  // judiciary receipt: the existing printable pages, keyed by the case fee id / internal payment id
  const onViewReceipt = (row) => {
    setPreviewUrl(
      row.source === "casefee"
        ? `${Constants.baseurl}/receipt/${row.id}`
        : `${Constants.baseurl}/receipt-internal/JISI${row.id}`,
    );
  };

  // revenue receipt: the gateway receipt link saved on the bill (ReceiptUrl)
  const onRevenueReceipt = (row) => setPreviewUrl(row.receiptUrl);

  const fmtDate = (d) => (d ? Constants.momentDate(d).format("DD MMM YYYY, hh:mm a") : "");

  return (
    <div style={{ padding: 16 }}>
      {/* ---------- filters ---------- */}
      <div className="panel">
        <div className="title">Cashier Financial Report</div>
        <div className="panel-body">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {PERIODS.map((p) => (
              <JisButton
                key={p.key}
                variant={period === p.key ? "primary" : "outline"}
                onClick={() => onPeriod(p.key)}
              >
                {p.label}
              </JisButton>
            ))}
          </div>
          <style>{CLEAR_DATE_CSS}</style>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div className="input-group" style={{ minWidth: 160 }}>
              <label>From</label>
              <div className="input-wrapper">
                <input type="date" name="fromDate" value={filters.fromDate} onChange={onChange} />
                {filters.fromDate && (
                  <button type="button" className="clear-date" title="Clear date" aria-label="Clear date" onClick={() => clearDate("fromDate")}>
                    <DynamicIcon iname="x" size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="input-group" style={{ minWidth: 160 }}>
              <label>To</label>
              <div className="input-wrapper">
                <input type="date" name="toDate" value={filters.toDate} onChange={onChange} />
                {filters.toDate && (
                  <button type="button" className="clear-date" title="Clear date" aria-label="Clear date" onClick={() => clearDate("toDate")}>
                    <DynamicIcon iname="x" size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="select-group" style={{ minWidth: 160 }}>
              <label>Payment Type</label>
              <select name="paymentType" value={filters.paymentType} onChange={onChange}>
                <option value="all">All</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
            </div>
            <div className="select-group" style={{ minWidth: 180 }}>
              <label>Division</label>
              <select name="divisionId" value={filters.divisionId} onChange={onChange}>
                <option value="">All Divisions</option>
                {frequentData?.divisions?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group" style={{ flex: 1, minWidth: 220 }}>
              <label>Search (payer name, suit number or bill reference)</label>
              <div className="input-wrapper">
                <DynamicIcon iname="search" />
                <input
                  name="search"
                  value={filters.search}
                  placeholder="e.g. John Doe, LD/1234/2025, bill reference, JISE1024"
                  onChange={onChange}
                  onKeyDown={(e) => e.key === "Enter" && onGenerate()}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="panel-btn">
          <JisButton onClick={onGenerate}>Generate Report</JisButton>
        </div>
      </div>

      {err && <div className="error">{err}</div>}
      {notFound && <div className="error" style={{ background: "#fff4e5", color: "#8a4b00", padding: "8px 12px", borderRadius: 8 }}>{notFound}</div>}

      {/* ---------- summary ---------- */}
      {summary && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, margin: "16px 0" }}>
          {[
            { label: "Internal Payments", value: money(summary.internalTotal), style: TYPE_STYLE.Internal },
            { label: "External Payments", value: money(summary.externalTotal), style: TYPE_STYLE.External },
            { label: "Grand Total", value: money(summary.grandTotal), style: { background: "#eef7ee", border: "1px solid #b9dfb9", color: "#1b5e20" } },
            { label: "Bills Paid", value: Number(summary.billCount || 0).toLocaleString(), style: { background: "#f4f4f4", border: "1px solid #ddd", color: "#333" } },
          ].map((c) => (
            <div
              key={c.label}
              style={{
                flex: "1 1 200px",
                borderRadius: 10,
                padding: "12px 16px",
                ...c.style,
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.85 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ---------- transactions ---------- */}
      <div className="table">
        <div className="section-cases" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span>
            Transactions
          </span>
          {count > 0 && (
            <div className="exportdiv" style={{ display: "flex", gap: 8 }}>
              <img src={wordicon} alt="Word" title="Download Word" onClick={() => onExport("msword")} style={{ cursor: "pointer" }} />
              <img src={excelicon} alt="Excel" title="Download Excel" onClick={() => onExport("excel")} style={{ cursor: "pointer" }} />
              <img src={pdficon} alt="PDF" title="Download PDF" onClick={() => onExport("pdf")} style={{ cursor: "pointer" }} />
            </div>
          )}
        </div>

        {items.length === 0 && !loading && <JisEmptyList title="No payments found for the selected period" />}

        {items.length > 0 && (
          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table className="case-table">
              <thead>
                <tr>
                  <th className="col-id">S/N</th>
                  <th className="col-medium">Bill Ref</th>
                  <th className="col-medium">Transaction ID</th>
                  <th className="col-title">Payer Name</th>
                  <th className="col-medium">Payer ID</th>
                  <th className="col-medium">Suit Number</th>
                  <th className="col-medium">Type</th>
                  <th className="col-medium">Date</th>
                  <th className="col-medium" style={{ textAlign: "right" }}>Amount</th>
                  <th className="col-title">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={`${r.jisReference || r.billReference}-${r.paymentType}`}>
                    <td>{pageIndex * pageSize + i + 1}</td>
                    <td style={{ wordBreak: "break-all" }}>
                      {r.billReference}
                      {r.jisReference && r.jisReference !== r.billReference && (
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{r.jisReference}</div>
                      )}
                    </td>
                    <td>{r.transactionId || "-"}</td>
                    <td>{r.payerName || "-"}</td>
                    <td>{r.payerId || "-"}</td>
                    <td>{r.suitNumber || <span style={{ opacity: 0.7 }}>{r.purpose || "-"}</span>}</td>
                    <td>
                      <span
                        style={{
                          ...TYPE_STYLE[r.paymentType],
                          borderRadius: 12,
                          padding: "2px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.paymentType}
                      </span>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{fmtDate(r.transactionDate)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>{money(r.amount)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <JisButton variant="plain" onClick={() => onViewReceipt(r)}>
                        Judiciary
                      </JisButton>{" "}
                      {r.receiptUrl && (
                        <JisButton variant="plain" onClick={() => onRevenueReceipt(r)}>
                          Revenue
                        </JisButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {count > 0 && (
          <Pagenated
            onChangePageSize={onPageSize}
            size={pageSize}
            total={count}
            maxPage={20}
            onPick={onPagePick}
          />
        )}
      </div>

      <BackLoader open={loading} />
      {previewUrl && (
        <JisDialogForm title="Receipt" onClose={() => setPreviewUrl("")}>
          <div style={{ height: "80vh", minWidth: "min(800px, 85vw)" }}>
            <PdfViewer pdfUrl={previewUrl} />
          </div>
        </JisDialogForm>
      )}
    </div>
  );
}

export default CashierReport;
